import React, {useState, useEffect, useRef} from 'react';
import {Button, Form, Input, message, Checkbox} from 'antd';
import {LockOutlined, MailOutlined} from '@ant-design/icons';
import {useAppDispatch} from "../../hooks";
import {loginUser} from "../../store/thunks/userThunks";
import log from 'loglevel';
import { debounce } from 'lodash';

const LAST_LOGIN_EMAIL_KEY = 'last_login_email';

interface SigninFormProps {
    onClose: () => void;
    onForgetPassword: () => void;
}

interface FormValues {
    email: string;
    password: string;
}

const SigninForm: React.FC<SigninFormProps> = ({onClose, onForgetPassword}) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();

    const debouncedLogin = useRef(
        debounce(async (values: FormValues) => {
            try {
                const response = await dispatch(loginUser({
                    email: values.email, 
                    password: values.password,
                })).unwrap();
                if (response.code === 200) {
                    localStorage.setItem(LAST_LOGIN_EMAIL_KEY, values.email);
                    onClose();
                    window.location.reload();
                } else {
                    message.error(response.message || '登录失败');
                }
            } catch (error: any) {
                log.debug(error);
                message.error(error?.message || '登录失败，请稍后重试');
            } finally {
                setLoading(false);
            }
        }, 1000)
    ).current;

    useEffect(() => {
        // 从本地存储获取上次登录的邮箱
        const lastEmail = localStorage.getItem(LAST_LOGIN_EMAIL_KEY);
        if (lastEmail) {
            form.setFieldsValue({ email: lastEmail });
        }

        return () => {
            debouncedLogin.cancel();
        };
    }, [form]);

    const onFinish = (values: FormValues) => {
        setLoading(true);
        debouncedLogin(values);
    };

    return (<Form
            form={form}
            name="login"
            onFinish={onFinish}
        >
            <Form.Item
                name="email"
                rules={[{ type: 'email', message: '请输入有效的邮箱地址！' }, {
                    required: true, message: '请输入您的邮箱！'
                }]}
            >
                <Input prefix={<MailOutlined/>} placeholder="邮箱" style={{ height: '40px' }}/>
            </Form.Item>
            <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入您的密码！' }, {
                    min: 8, max: 16, message: '密码长度必须在8到16个字符之间！'
                }]}
                style={{marginBottom: 0}}>
                <Input.Password prefix={<LockOutlined/>} placeholder="密码" style={{ height: '40px' }}/>
            </Form.Item>
            <Form.Item style={{display: 'flex', justifyContent: 'space-between', marginBottom: 0}}>
                <Button type="link" style={{padding: 0}} onClick={onForgetPassword}>忘记密码</Button>
            </Form.Item>
            <Form.Item>
                <Button type="primary" htmlType="submit" style={{ height: '40px' }} loading={loading} block>
                    登录
                </Button>
            </Form.Item>
            <div>
            注册登录即表示同意
                    <a href={`${process.env.PUBLIC_URL}/terms.html`} target="_blank" rel="noopener noreferrer">用户协议</a>
                    和
                    <a href={`${process.env.PUBLIC_URL}/privacy.html`} target="_blank" rel="noopener noreferrer">隐私政策</a>
            </div>
        </Form>);
};

export default SigninForm;